import { TFunction } from "i18next";
import { AvailableTimesSection } from "types/available-times";
import { PaymentCalculationRule, PaymentRule } from "types/payment";
import { getPaymentUnitTranslation } from "utils/payment-utils";

export type ActivityBadgeSection = Pick<AvailableTimesSection, "timeSets">;

export type PaymentBadgeTranslationKey =
    | "activitySummary.paymentBadge.prepayment"
    | "activitySummary.paymentBadge.payOnSite";

export function getPaymentBadgeTranslationKeyForPaymentRules(
    paymentRules: Array<PaymentRule | null | undefined>
): PaymentBadgeTranslationKey | null {
    if (paymentRules.some((paymentRule) => paymentRule === PaymentRule.Prepayment)) {
        return "activitySummary.paymentBadge.prepayment";
    }

    if (paymentRules.some((paymentRule) => paymentRule === PaymentRule.PayAtLocation)) {
        return "activitySummary.paymentBadge.payOnSite";
    }

    return null;
}

export function getPaymentBadgeTranslationKey(
    sections: ActivityBadgeSection[]
): PaymentBadgeTranslationKey | null {
    return getPaymentBadgeTranslationKeyForPaymentRules(
        sections.flatMap((section) => section.timeSets.map((timeSet) => timeSet.paymentRule))
    );
}

/**
 * Formats an array of activity durations into a human-readable badge format.
 *
 * @param {number[]} minutesArray - Array of durations in minutes.
 * @returns {string} Formatted time range (e.g., "1 tim 30 min" or "1 tim - 2 tim").
 */
export function getFormattedActivityLength(
    minutesArray: number[],
    t: (key: string) => string
): string {
    if (minutesArray.length === 0) return "";

    const min = Math.min(...minutesArray);
    const max = Math.max(...minutesArray);

    const formatValue = (minutes: number): string => {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;

        if (hours === 0) return `${remainingMinutes} ${t("minutesAbbreviated")}`;
        if (remainingMinutes === 0) return `${hours} ${t("hoursAbbreviated")}`;
        return `${hours} ${t("hoursAbbreviated")} ${remainingMinutes} ${t("minutesAbbreviated")}`;
    };

    const formattedMin = formatValue(min);
    const formattedMax = formatValue(max);

    return formattedMin === formattedMax ? formattedMin : `${formattedMin} - ${formattedMax}`;
}

/**
 * Extracts unformatted activity durations from one or more sections.
 *
 * Single section (activity): returns one duration per timeSet so the caller can display a range
 * of selectable options.
 *
 * Multiple sections (experience): each section is a sequential step — their durations are summed
 * into a single total so the badge shows the full experience length (e.g. "1h 55min").
 *
 * @param {AvailableTimesSection[]} sections - The sections containing time sets.
 * @param {number} maximumGapTime - Gap for a slot option whose first time set carries none.
 * @returns {number[]} Array of durations in minutes.
 */
export function getSectionBookingDurations(
    sections: ActivityBadgeSection[],
    maximumGapTime = 0
): number[] {
    if (sections.length <= 1) {
        const activityLength: number[] = [];
        sections[0]?.timeSets.forEach((timeSet) => {
            const firstTimeSlot = timeSet.times.at(0);
            if (!firstTimeSlot) return;
            const duration =
                firstTimeSlot.end.diff(firstTimeSlot.start, "minutes") - timeSet.recoupTime;
            activityLength.push(duration);
        });
        return activityLength;
    }

    // Experience: timeSets are matched by index across sections — index N in section A pairs with
    // index N in section B. Sum durations at each index to get one total per "slot option".
    // Add gap time for each transition between activities.
    const slotCount = Math.max(...sections.map((s) => s.timeSets.length));
    const totals: number[] = [];
    for (let i = 0; i < slotCount; i++) {
        let slotTotal = 0;
        let hasAny = false;
        for (const section of sections) {
            const timeSet = section.timeSets.at(i) ?? section.timeSets.at(0);
            if (!timeSet) continue;
            const firstTimeSlot = timeSet.times.at(0);
            if (!firstTimeSlot) continue;
            slotTotal += firstTimeSlot.end.diff(firstTimeSlot.start, "minutes");
            hasAny = true;
        }
        if (hasAny) {
            const lastSection = sections[sections.length - 1];
            const lastTimeSet = lastSection.timeSets.at(i) ?? lastSection.timeSets.at(0);
            slotTotal -= lastTimeSet?.recoupTime ?? 0;
            const firstTimeSet = sections[0].timeSets.at(i) ?? sections[0].timeSets.at(0);
            const gapTotal =
                (firstTimeSet?.maximumGapTime ?? maximumGapTime) * (sections.length - 1);
            totals.push(slotTotal);
            totals.push(slotTotal + gapTotal);
        }
    }
    return totals;
}

/**
 * Extracts formatted activity price from one or more sections.
 * We have decided to not show no show fee since there might be way to many on a section and it does not provided any useful information until we are in the payment details section of the user flow.
 *
 * Single section (activity): shows a price range or "from X" if timeSets have different prices.
 *
 * Multiple sections (experience): each section is a sequential step — their prices are summed
 * into a single total so the badge shows the combined cost.
 *
 * @param {AvailableTimesSection[]} sections - The sections containing time sets.
 * @returns {string | null} Formatted price string, or null if calculation rules differ (single-section only).
 */
export function getFormattedActivityPrice(
    sections: ActivityBadgeSection[],
    t: TFunction<"translation", undefined>
): string | null {
    if (sections.length > 1) {
        // Experience: timeSets are matched by index across sections. Sum each index's payment
        // amounts to get one total per "slot option", then apply from/single logic on those totals.
        const slotCount = Math.max(...sections.map((s) => s.timeSets.length));
        type SlotTotal = {
            amount: number;
            calculationRule: PaymentCalculationRule;
            representativeTimeSet: (typeof sections)[0]["timeSets"][0];
        };
        const slotTotals: SlotTotal[] = [];

        for (let i = 0; i < slotCount; i++) {
            let slotAmount = 0;
            let repTimeSet: (typeof sections)[0]["timeSets"][0] | null = null;
            let repCalcRule: PaymentCalculationRule | null = null;

            for (const section of sections) {
                const timeSet = section.timeSets.at(i) ?? section.timeSets.at(0);
                if (
                    !timeSet ||
                    timeSet.paymentAmount <= 0 ||
                    timeSet.paymentRule === null ||
                    timeSet.paymentRule === PaymentRule.NoShowFee ||
                    timeSet.paymentRule === PaymentRule.NoPayment
                )
                    continue;
                slotAmount += timeSet.paymentAmount;
                if (repTimeSet === null) {
                    repTimeSet = timeSet;
                    repCalcRule = timeSet.paymentCalculationRule;
                }
            }

            if (slotAmount > 0 && repTimeSet && repCalcRule !== null) {
                slotTotals.push({
                    amount: slotAmount,
                    calculationRule: repCalcRule,
                    representativeTimeSet: repTimeSet,
                });
            }
        }

        if (slotTotals.length === 0) return "";

        const uniqueCalcRules = new Set(slotTotals.map((s) => s.calculationRule));
        if (uniqueCalcRules.size > 1) return null;

        const minAmount = Math.min(...slotTotals.map((s) => s.amount));
        return `${t("from")} ${t("currency.onlyAmount", { value: minAmount, display: "symbol" })}`;
    }

    // Single activity: existing range logic across timeSets
    const paymentRules =
        sections[0]?.timeSets
            .map((timeSet) => {
                if (
                    timeSet.paymentAmount > 0 &&
                    timeSet.paymentRule !== null &&
                    timeSet.paymentRule !== PaymentRule.NoShowFee &&
                    timeSet.paymentRule !== PaymentRule.NoPayment
                ) {
                    return {
                        amount: timeSet.paymentAmount,
                        calculationRule: timeSet.paymentCalculationRule,
                        formatted: `${t("currency.onlyAmount", { value: timeSet.paymentAmount, display: "symbol" })}/${getPaymentUnitTranslation(timeSet)}`,
                    };
                }
                return null;
            })
            .filter(
                (
                    rule
                ): rule is {
                    amount: number;
                    calculationRule: PaymentCalculationRule;
                    formatted: string;
                } => rule !== null
            ) ?? [];

    if (paymentRules.length === 0) return "";

    // Check if all payment rules have the same calculation rule
    const uniqueCalculationRules = Array.from(
        new Set(paymentRules.map((rule) => rule.calculationRule))
    );
    if (uniqueCalculationRules.length > 1) {
        // Different calculation rules - cannot meaningfully display a combined price
        return null;
    }

    // Get unique prices
    const uniquePrices = Array.from(new Set(paymentRules.map((rule) => rule.amount)));

    if (uniquePrices.length === 1) {
        // Single price - return the formatted price directly
        return paymentRules[0].formatted;
    } else {
        // Multiple different prices - show from the lowest price
        const minPrice = Math.min(...uniquePrices);
        const minPriceRule = paymentRules.find((rule) => rule.amount === minPrice);
        return `${t("from")} ${minPriceRule?.formatted}`;
    }
}

/**
 * Formats the minimum and maximum group sizes from the first section's time sets into a readable range.
 * Group size is consistent across activities within an experience, so only the first section is used.
 *
 * @param {AvailableTimesSection[]} sections - The sections containing time sets. Only the first is used for group size.
 * @param {(key: string) => string} t - Translation function for localizing the unit (e.g., "persons").
 * @returns {string} A formatted string representing the group size range (e.g., "2 - 10 persons"), or an empty string if values are invalid.
 */
export function getFormattedGroupSizeRange(
    sections: ActivityBadgeSection[],
    t: (key: string) => string
): string {
    const section = sections[0];
    if (!section) return "";
    const maxSizes = section.timeSets.map((timeSet) => timeSet.maxGroupSize);
    const minSizes = section.timeSets.map((timeSet) => timeSet.minGroupSize);

    const maxNum = Math.max(...maxSizes);
    const minNum = Math.min(...minSizes);

    if (!(maxNum > 0) || !(minNum > 0)) return "";

    return `${minNum} - ${maxNum} ${t("persons")}`;
}
